import React, { useState } from 'react';
import { Key, Copy, AlertTriangle, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function PlatformApiKeys() {
  const tenant = JSON.parse(localStorage.getItem('platform_tenant') || '{}');
  const firstKey = localStorage.getItem('platform_first_api_key');
  const [showTestKey, setShowTestKey] = useState(false);

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied');
  };

  return (
    <div className="max-w-2xl" data-testid="platform-api-keys-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>API Keys</h1>
        <p className="text-gray-500 mt-1">Manage your test and live API credentials</p>
      </div>

      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3" data-testid="api-key-warning">
        <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Keep your API keys secure</p>
          <p className="text-sm text-amber-600">Never share keys in client-side code, public repos, or chat messages. Use environment variables.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4" data-testid="test-key-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center">
            <Key size={20} className="text-yellow-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Test API Key</h3>
            <p className="text-xs text-gray-500">For sandbox testing — fake banking data</p>
          </div>
          <span className="ml-auto text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">Sandbox</span>
        </div>

        {firstKey ? (
          <div className="flex items-center gap-2 mb-3">
            <code className="flex-1 bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-mono text-gray-700 border truncate" data-testid="test-key-display">
              {showTestKey ? firstKey : firstKey.slice(0, 16) + '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
            </code>
            <button onClick={() => setShowTestKey(!showTestKey)} className="p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="toggle-test-key-btn">
              {showTestKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            <button onClick={() => copyKey(firstKey)} className="p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="copy-test-key-btn">
              <Copy size={16} />
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-3">Your test API key was shown at signup. Sign up again to get a new one.</p>
        )}

        <p className="text-xs text-gray-400">
          Base URL: <code className="bg-gray-100 px-1.5 py-0.5 rounded">https://www.pebbles.financial/api/platform/v1</code>
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="live-key-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
            <Shield size={20} className="text-green-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Live API Key</h3>
            <p className="text-xs text-gray-500">For production — real banking transactions</p>
          </div>
          <span className={`ml-auto text-xs px-2 py-1 rounded-full font-medium ${
            tenant.is_live ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
          }`}>
            {tenant.is_live ? 'Active' : 'Not available'}
          </span>
        </div>

        {tenant.is_live ? (
          <p className="text-sm text-gray-600 mb-2">Your live key was shown once at approval. Contact support if you need a new one.</p>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-sm text-gray-500 mb-2">Complete verification to get your live API key</p>
            <Link to="/platform/go-live" className="text-sm font-semibold text-[#E8678B] hover:underline" data-testid="start-verification-link">Start verification &rarr;</Link>
          </div>
        )}
      </div>
    </div>
  );
}
